// ────────────────────────────────────────────────────────────
// UI STATE STORE
// ────────────────────────────────────────────────────────────

import { create } from 'zustand';
import { uiRepo } from '../services/persistence';
import type { CenterTab, Lens, OpenTab } from '../types';

interface UIStateInternal {
  openTabs: OpenTab[];
  activeDealId: string | null;
  showDealIndex: boolean;
  // Per-deal lens override. When null, lens auto-derives from pinnedTab.
  lensOverride: Record<string, Lens | null>;
  // Per-deal active center tab
  centerTab: Record<string, CenterTab>;

  load: () => Promise<void>;
  openDeal: (dealId: string) => void;
  closeDeal: (dealId: string) => void;
  setActive: (dealId: string | null) => void;
  setCenterTab: (dealId: string, tab: CenterTab) => void;
  setLensOverride: (dealId: string, lens: Lens | null) => void;
  setShowDealIndex: (on: boolean) => void;
}

const persistKeys: (keyof UIStateInternal)[] = ['openTabs', 'activeDealId', 'lensOverride', 'centerTab'];

async function persist(state: UIStateInternal) {
  const minimal: any = {};
  for (const k of persistKeys) minimal[k] = (state as any)[k];
  await uiRepo.save({ ...minimal, showDealIndex: false });
}

export const useUIStore = create<UIStateInternal>((set, get) => ({
  openTabs: [],
  activeDealId: null,
  showDealIndex: false,
  lensOverride: {},
  centerTab: {},

  async load() {
    const u = await uiRepo.load();
    if (u) {
      set({
        openTabs: u.openTabs ?? [],
        activeDealId: u.activeDealId ?? null,
        lensOverride: u.lensOverride ?? {},
        centerTab: (u as any).centerTab ?? {},
      });
    }
  },

  openDeal(dealId) {
    const cur = get();
    const existing = cur.openTabs.find(t => t.dealId === dealId);
    const openTabs = existing ? cur.openTabs : [...cur.openTabs, { dealId }];
    const newState = { ...cur, openTabs, activeDealId: dealId, showDealIndex: false };
    set(newState);
    void persist(newState);
  },

  closeDeal(dealId) {
    const cur = get();
    const openTabs = cur.openTabs.filter(t => t.dealId !== dealId);
    const activeDealId = cur.activeDealId === dealId
      ? (openTabs[openTabs.length - 1]?.dealId ?? null)
      : cur.activeDealId;
    const newState = { ...cur, openTabs, activeDealId };
    set(newState);
    void persist(newState);
  },

  setActive(dealId) {
    const cur = get();
    set({ ...cur, activeDealId: dealId, showDealIndex: false });
    void persist({ ...cur, activeDealId: dealId });
  },

  setCenterTab(dealId, tab) {
    const cur = get();
    const centerTab = { ...cur.centerTab, [dealId]: tab };
    set({ ...cur, centerTab });
    void persist({ ...cur, centerTab });
  },

  setLensOverride(dealId, lens) {
    const cur = get();
    const lensOverride = { ...cur.lensOverride, [dealId]: lens };
    set({ ...cur, lensOverride });
    void persist({ ...cur, lensOverride });
  },

  setShowDealIndex(on) { set(s => ({ ...s, showDealIndex: on })); },
}));
